import React, {FC, useMemo, useState} from "react";
import {Group, Line, Text} from "react-konva";
import {useDispatch, useSelector} from "react-redux";
import {EditorLine, TopViewWallAreaMeasurement} from "../../models/editor";
import {selectActiveTopViewWallAreaId, topViewWallAreaActions} from "./topViewWallAreaSlice";
import {selectExportInProgress, selectSelectionState, selectZoomState} from "../view/viewSlice";
import {getDashTypePointArray, getPointDraggingLines} from "../../utils";
import {PointDragState} from "../../../../../base-konva/types";
import {getPointerPosition} from "../../../../../base-konva/utils";
import {selectScale} from "../scale/scaleSlice";
import {TopViewWallAreaHelper} from "./TopViewWallAreaHelper";
import {useUserSettingsDataContext} from "../../../../../../providers/UserSettingsProvider";
import {formatArea, formatLength} from "../../../../../../utils/TextUtils";
import {useGetHighlightShapeStyle} from "../../hooks/highlight";
import {useGetSelectionShapeStyle, useSelectSetter} from "../../hooks/select";
import {LineDistanceMark} from "../../components/LineDistanceMark";
import {LinePoint} from "../../components/LinePoint";
import {useCursorStyle} from "../../hooks/useCursorStyle";
import {CursorStyle, DrawingItemType} from "../../../../../base-konva/enums";
import {EditorTool} from "../../models/enums";
import {selectActiveTool, configActions} from "../config/configSlice";
import {getId} from "../../../../../../utils";

type TopViewWallAreaElementProps = {
	measurement: TopViewWallAreaMeasurement
	pointerLocation?: {x: number; y: number}
	isPointerOverLayer?: boolean
}

const _TopViewWallAreaElement: FC<TopViewWallAreaElementProps> = function({measurement, pointerLocation, isPointerOverLayer}) {
	const dispatch = useDispatch();
	const activeId = useSelector(selectActiveTopViewWallAreaId);
	const exportInProgress = useSelector(selectExportInProgress);
	const scale = useSelector(selectScale);
	const {settings} = useUserSettingsDataContext();
	const {rotation: zoomRotation} = useSelector(selectZoomState);
	const {changeCursor} = useCursorStyle();
	const [pointDragState, setPointDragState] = useState<PointDragState>({isDragging: false});

	const isActive = activeId === measurement.id;
	const activeTool = useSelector(selectActiveTool);
	const visible = measurement.visible || exportInProgress;
	const {stroke, strokeWidth, dashType} = measurement.style;
	const {getHighlightStyle} = useGetHighlightShapeStyle();
	const highlightStyle = getHighlightStyle(id => id === measurement.id);
	const {getSelectionStyle} = useGetSelectionShapeStyle();
	const selectActions = useSelectSetter();
	const selectionState = useSelector(selectSelectionState);

	const pointToSegmentId = useMemo(() => {
		const map = new Map<string, string>();
		if (!isActive) return map;
		for (const segment of measurement.wallSegments) {
			for (const line of segment.lines) {
				map.set(line.from.id, segment.id);
				map.set(line.to.id, segment.id);
			}
		}
		return map;
	}, [isActive, measurement.wallSegments]);

	const previewPoints = getPreviewPoints();

	function getPreviewPoints(): number[] | null {
		if (!isActive || !isPointerOverLayer || !pointerLocation) return null;
		const activeSegment = measurement.wallSegments.find(s => s.id === measurement.activeSegmentId);
		if (!activeSegment || activeSegment.closed || !activeSegment.lastMouseUpPosition) return null;

		const lastPos = activeSegment.lines.length > 0
			? activeSegment.lines[activeSegment.lines.length - 1].to.position
			: activeSegment.lastMouseUpPosition;

		return [lastPos.x, lastPos.y, pointerLocation.x, pointerLocation.y];
	}

	const previewEditorLine: EditorLine | null = previewPoints ? (() => {
		const [x1, y1, x2, y2] = previewPoints;
		const dx = x2 - x1;
		const dy = y2 - y1;
		return {
			id: 'preview',
			from: {id: 'preview-from', position: {x: x1, y: y1}},
			to: {id: 'preview-to', position: {x: x2, y: y2}},
			distance: Math.hypot(dx, dy),
			angle: Math.atan2(dy, dx) * 180 / Math.PI,
			center: {x: (x1 + x2) / 2, y: (y1 + y2) / 2},
		};
	})() : null;

	const formattedValue = TopViewWallAreaHelper.getFormattedValue(measurement, scale, settings?.measurementSystem);

	function getLabelPosition(): {x: number; y: number} | null {
		for (const segment of measurement.wallSegments) {
			if (segment.lines.length > 0) {
				const line = segment.lines[Math.floor(segment.lines.length / 2)];
				return {x: line.center.x + 8, y: line.center.y - 30};
			}
		}
		return null;
	}

	const labelPos = getLabelPosition();
	const orderedPoints = isActive ? TopViewWallAreaHelper.getOrderedUniquePoints(measurement) : [];

	function handleLineClick(segmentId: string, lineId: string) {
		const actionId = getId();
		if (activeTool === EditorTool.MEASUREMENT_SELECT) {
			dispatch(configActions.switchEditorTool({editorTool: EditorTool.TOP_VIEW_WALL_AREA}));
			dispatch(configActions.switchEditorTool({editorTool: EditorTool.MEASUREMENT_SELECT}));
		}
		dispatch(topViewWallAreaActions.activate({id: measurement.id, actionId}));
		selectActions.clearSelection();
		selectActions.setSelection(DrawingItemType.TOP_VIEW_WALL_AREA, measurement.id, segmentId, [lineId], actionId);
	}

	return (
		<Group visible={visible}>
			{measurement.wallSegments.map(segment => {
				const renderLines = isActive && pointDragState.isDragging
					? getPointDraggingLines(segment.lines, pointDragState)
					: segment.lines;
				if (renderLines.length === 0) return null;

				// Build closing line for closed segments so it can be individually selected/deleted
				const closingLine: EditorLine | null = segment.closed && renderLines.length >= 1 ? (() => {
					const first = renderLines[0].from;
					const last = renderLines[renderLines.length - 1].to;
					const dx = first.position.x - last.position.x;
					const dy = first.position.y - last.position.y;
					const dist = Math.hypot(dx, dy);
					if (dist === 0) return null;
					return {
						id: `${segment.id}-closing`,
						from: last,
						to: first,
						distance: dist,
						angle: Math.atan2(dy, dx) * 180 / Math.PI,
						center: {x: (last.position.x + first.position.x) / 2, y: (last.position.y + first.position.y) / 2},
					};
				})() : null;

				const allRenderLines = closingLine ? [...renderLines, closingLine] : renderLines;

				return (
					<React.Fragment key={segment.id}>
						{allRenderLines.map(line => {
							const lineSelected = selectionState.some(item =>
								item.type === DrawingItemType.TOP_VIEW_WALL_AREA &&
								item.itemId === measurement.id &&
								item.subItemIds.some(id => id === line.id)
							);
							const lineStyle = {
								...highlightStyle,
								...getSelectionStyle(() => lineSelected),
							};
							const isClickable = activeTool === EditorTool.MEASUREMENT_SELECT;
							return (
								<Line
									key={line.id}
									points={[line.from.position.x, line.from.position.y, line.to.position.x, line.to.position.y]}
									stroke={stroke}
									strokeWidth={isActive ? strokeWidth + 1 : strokeWidth}
									dash={getDashTypePointArray(dashType, strokeWidth)}
									lineCap="round"
									lineJoin="round"
									fillEnabled={false}
									onMouseEnter={evt => changeCursor({event: evt, cursor: CursorStyle.POINTER})}
									onMouseLeave={evt => changeCursor({event: evt, cursor: CursorStyle.DEFAULT})}
									onClick={() => isClickable && handleLineClick(segment.id, line.id)}
									onTap={() => isClickable && handleLineClick(segment.id, line.id)}
									{...lineStyle}
								/>
							);
						})}
						{renderLines.map(line => (
							<LineDistanceMark
								key={line.id}
								line={line}
								position={line.center}
								listening={false}
							/>
						))}
						{closingLine && (
							<LineDistanceMark key={closingLine.id} line={closingLine} position={closingLine.center} listening={false}/>
						)}
					</React.Fragment>
				);
			})}
			{previewPoints && previewEditorLine && (
				<>
					<Line
						points={previewPoints}
						stroke={stroke}
						strokeWidth={strokeWidth}
						dash={[4, 4]}
						opacity={0.6}
					/>
					<LineDistanceMark
						line={previewEditorLine}
						position={previewEditorLine.center}
						listening={false}
					/>
				</>
			)}
			{isActive && labelPos && (
				<Group x={labelPos.x} y={labelPos.y} rotation={-zoomRotation} listening={false}>
					<Text
						x={0} y={0}
						text={formattedValue}
						fontSize={12}
						fill={stroke}
						listening={false}
					/>
					{measurement.wallDeductions.map((deduction, i) => (
						<Text
							key={deduction.id}
							x={0}
							y={14 + i * 13}
							text={`- ${formatArea(deduction.area, settings?.measurementSystem)}`}
							fontSize={11}
							fill={stroke}
							opacity={0.6}
							listening={false}
						/>
					))}
				</Group>
			)}
			{orderedPoints.map((point, index) => {
				const pointLabel = TopViewWallAreaHelper.getPointLabel(index);
				const height = measurement.heightMap[point.id];
				const isDraggingThisPoint =
					pointDragState.isDragging && pointDragState.dragPointId === point.id;
				const labelAnchor = isDraggingThisPoint && pointDragState.dragPointCurrentLocation
					? pointDragState.dragPointCurrentLocation
					: point.position;
				return (
					<React.Fragment key={point.id}>
						<LinePoint
							point={point}
							isActive={pointDragState.dragPointId === point.id}
							onMouseEnter={evt => changeCursor({event: evt, cursor: CursorStyle.MOVE})}
							onMouseLeave={evt => changeCursor({event: evt, cursor: CursorStyle.DEFAULT})}
							onDragStart={evt => {
								const pos = getPointerPosition(evt) ?? point.position;
								setPointDragState({
									isDragging: true,
									dragPointId: point.id,
									dragPointCurrentLocation: pos,
								});
							}}
							onDragMove={evt => {
								const pos = getPointerPosition(evt);
								if (!pos) return;
								setPointDragState(prev => ({
									...prev,
									dragPointCurrentLocation: pos,
								}));
							}}
							onDragEnd={evt => {
								const pos = getPointerPosition(evt) ?? pointDragState.dragPointCurrentLocation ?? point.position;
								const segmentId = pointToSegmentId.get(point.id);
								if (segmentId) {
									dispatch(topViewWallAreaActions.updatePointPosition({
										segmentId,
										pointId: point.id,
										newPosition: pos,
									}));
								}
								setPointDragState({isDragging: false});
							}}
						/>
						<Group
							x={labelAnchor.x + 4}
							y={labelAnchor.y - 18}
							rotation={-zoomRotation}
							listening={false}
						>
							{height !== undefined && (
								<Text
									x={0} y={-12}
									text={formatLength(height, settings?.measurementSystem)}
									fontSize={11}
									fill={stroke}
									listening={false}
								/>
							)}
							<Text
								x={0} y={0}
								text={pointLabel}
								fontSize={11}
								fill={stroke}
								opacity={height !== undefined ? 1 : 0.45}
								listening={false}
							/>
						</Group>
					</React.Fragment>
				);
			})}
		</Group>
	);
};

export const TopViewWallAreaElement = React.memo(_TopViewWallAreaElement);
